import React, { useState, useEffect } from "react";
import { Loader2, DollarSign, Calendar, FileText, CheckCircle, AlertCircle } from "lucide-react";
import CreatableSelect from 'react-select/creatable';
import { useUser } from "../../../context/UserContext";
import commissionsService from "../../../services/commissionsService";
import { sanitizeText, sanitizeEmail, sanitizePhone } from "../../../utils/sanitization";
import { decodeHtmlEntities } from "../../../utils/textUtils";
import "../../../styles/modern-design-system.css";
import toast from "../../../utils/toast";

// Cross-browser compatibility polyfills
const safeTrim = (str) => {
  if (typeof str !== 'string') return '';
  return str.trim ? str.trim() : str.replace(/^\s+|\s+$/g, '');
};

const CreateCommissionModal = ({ isOpen, onClose, onCommissionCreated }) => {
  const { user } = useUser();
  
  // Form state
  const [selectedTripOption, setSelectedTripOption] = useState(null);
  const [tripInput, setTripInput] = useState("");
  const [tripValue, setTripValue] = useState("");
  const [commissionRate, setCommissionRate] = useState("10");
  const [commissionAmount, setCommissionAmount] = useState("");
  const [status, setStatus] = useState("pending");
  const [expectedDate, setExpectedDate] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [bookingReference, setBookingReference] = useState("");
  const [supplier, setSupplier] = useState("Theme Park Resort");
  const [notes, setNotes] = useState("");
  
  // Data state
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch trips when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTrips();
      // Set default expected date to 30 days from now
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 30);
      setExpectedDate(defaultDate.toISOString().split('T')[0]);
    }
  }, [isOpen]);

  const fetchTrips = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/trips`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setTrips(data);
      }
    } catch (error) {
      console.error('Error fetching trips:', error);
      setTrips([]);
    }
  };

  // Prepare trip options for react-select
  const tripOptions = trips
    .filter(trip => trip.status === 'confirmed' || trip.status === 'completed')
    .map(trip => ({
      value: trip.tripid,
      label: `${decodeHtmlEntities(trip.tripname) || trip.fullname + "'s Trip"} - ${decodeHtmlEntities(trip.fullname)}`,
      trip,
    }));

  // Auto-calculate commission amount when rate or trip value changes
  useEffect(() => {
    if (tripValue && commissionRate) {
      const value = parseFloat(tripValue) || 0;
      const rate = parseFloat(commissionRate) || 0;
      const calculatedAmount = (value * rate / 100).toFixed(2);
      setCommissionAmount(calculatedAmount);
    }
  }, [tripValue, commissionRate]);

  const handleTripChange = (selectedOption) => {
    setSelectedTripOption(selectedOption);
    if (errors.trip) {
      setErrors(prev => ({ ...prev, trip: undefined }));
    }
    
    if (selectedOption?.trip) {
      // Auto-populate trip value if available
      if (selectedOption.trip.trip_value) {
        setTripValue(selectedOption.trip.trip_value.toString());
      }
      // Set supplier based on destination
      const destination = selectedOption.trip.destination;
      const supplierMap = {
        'disney_world': 'Disney Travel Company',
        'disneyland': 'Disneyland Resort',
        'universal_orlando': 'Universal Orlando Resort',
        'disney_cruise': 'Disney Cruise Line'
      };
      setSupplier(supplierMap[destination] || 'Theme Park Resort');
      
      // Regenerate booking reference with destination-specific prefix
      generateBookingReference(destination);
    }
  };

  const handleTripInputChange = (inputValue) => {
    setTripInput(inputValue);
    if (errors.trip) {
      setErrors(prev => ({ ...prev, trip: undefined }));
    }
  };

  // Auto-capitalize function for name fields
  const capitalizeName = (value) => {
    return value
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Currency formatting function
  const formatCurrency = (value) => {
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    
    // Handle multiple decimal points - keep only the first one
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      const integerPart = parts[0];
      const decimalPart = parts.slice(1).join('');
      return `${integerPart}.${decimalPart}`;
    }
    
    // If there's a decimal point, limit to 2 decimal places
    if (parts.length === 2) {
      const integerPart = parts[0];
      const decimalPart = parts[1].slice(0, 2);
      return `${integerPart}.${decimalPart}`;
    }
    
    return numericValue;
  };

  // Generate booking reference with destination prefix
  const generateBookingReference = (destinationOverride = null) => {
    const destinationPrefixes = {
      'disney_world': 'DW',
      'disneyland': 'DL', 
      'universal_orlando': 'UO',
      'disney_cruise': 'DC',
      'universal_hollywood': 'UH'
    };
    
    const dest = destinationOverride || selectedTripOption?.trip?.destination || 'unknown';
    const prefix = destinationPrefixes[dest] || 'BK';
    const randomSuffix = Math.random().toString(36).substr(2, 8).toUpperCase();
    const ref = `${prefix}-${randomSuffix}`;
    setBookingReference(ref);
  };

  useEffect(() => {
    if (isOpen && !bookingReference) {
      generateBookingReference();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!selectedTripOption) {
      newErrors.trip = "Please select a trip.";
    }

    if (!tripValue || parseFloat(tripValue) <= 0) {
      newErrors.tripValue = "Trip value must be greater than 0.";
    }

    if (!commissionRate || parseFloat(commissionRate) < 0 || parseFloat(commissionRate) > 100) {
      newErrors.commissionRate = "Commission rate must be between 0 and 100.";
    }

    if (!expectedDate) {
      newErrors.expectedDate = "Expected payment date is required.";
    }

    if (status === 'paid' && !paymentDate) {
      newErrors.paymentDate = "Payment date is required when status is 'Paid'.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      const commissionData = {
        tripId: selectedTripOption.value,
        clientId: selectedTripOption.trip.clientid,
        tripValue: parseFloat(tripValue),
        rate: parseFloat(commissionRate) / 100, // Convert percentage to decimal
        amount: parseFloat(commissionAmount),
        status: status,
        expectedDate: expectedDate,
        paymentDate: status === 'paid' ? paymentDate : null,
        bookingReference: safeTrim(sanitizeText(bookingReference)),
        supplier: safeTrim(sanitizeText(supplier)),
        notes: safeTrim(sanitizeText(notes))
      };

      const response = await commissionsService.createCommissionBooking(commissionData);

      if (response.success) {
        toast.success("Commission created successfully!");
        
        // Format the commission data for the parent component
        const newCommission = {
          id: response.data.commissionid,
          tripId: selectedTripOption.value,
          clientId: selectedTripOption.trip.clientid,
          clientName: selectedTripOption.trip.fullname,
          tripName: selectedTripOption.trip.tripname || `${selectedTripOption.trip.fullname}'s Trip`,
          tripValue: parseFloat(tripValue),
          commissionRate: parseFloat(commissionRate),
          commissionAmount: parseFloat(commissionAmount),
          status: status,
          expectedDate: expectedDate,
          paidDate: status === 'paid' ? paymentDate : null,
          bookingReference: safeTrim(bookingReference),
          supplier: safeTrim(supplier),
          notes: safeTrim(notes),
          tripStatus: selectedTripOption.trip.status,
          createdAt: new Date().toISOString()
        };

        onCommissionCreated(newCommission);
        handleClose();
      } else {
        throw new Error(response.error || 'Failed to create commission');
      }
    } catch (error) {
      console.error('Error creating commission:', error);
      toast.error(error.message || 'Failed to create commission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset form
      setSelectedTripOption(null);
      setTripInput("");
      setTripValue("");
      setCommissionRate("10");
      setCommissionAmount("");
      setStatus("pending");
      setExpectedDate("");
      setPaymentDate("");
      setBookingReference("");
      setSupplier("Theme Park Resort");
      setNotes("");
      setErrors({});
    }
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center px-4 bg-black/40 sm:px-6">
      <div className="p-5 w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-2xl animate-fade-in-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h2 className="mb-1 text-xl font-bold text-gray-900">
              Add New Commission
            </h2>
            <p className="text-xs text-gray-500">
              Create a commission record for a confirmed or completed trip
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2.5">
          {/* Trip Selection */}
          <div>
            <label htmlFor="tripSelect" className="block mb-1 text-sm font-medium text-gray-900">
              Trip *
            </label>
            <CreatableSelect
              id="tripSelect"
              options={tripOptions}
              value={selectedTripOption}
              onChange={handleTripChange}
              onInputChange={handleTripInputChange}
              inputValue={tripInput}
              placeholder="Select a confirmed or completed trip"
              isClearable
              isDisabled={loading}
              noOptionsMessage={() => 'No confirmed trips found'}
              classNamePrefix="react-select"
              styles={{
                control: (base, state) => ({ 
                  ...base, 
                  minHeight: 36,
                  fontSize: 14,
                  fontWeight: 500,
                  backgroundColor: '#374151',
                  border: `2px solid ${errors.trip ? '#ef4444' : state.isFocused ? '#3b82f6' : '#d1d5db'}`,
                  borderRadius: '8px',
                  padding: '0 12px',
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: errors.trip ? '#ef4444' : '#9ca3af'
                  }
                }),
                input: (base) => ({ ...base, color: '#ffffff' }),
                singleValue: (base) => ({ ...base, color: '#ffffff' }),
                placeholder: (base) => ({ ...base, color: '#d1d5db' }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? '#3b82f6' : '#ffffff',
                  color: state.isFocused ? 'white' : '#111827',
                })
              }}
            />
            {errors.trip && (
              <p className="mt-1 text-sm text-red-600">{errors.trip}</p>
            )}
          </div>

          {/* Trip Value and Commission Rate */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="tripValue" className="block mb-1 text-sm font-medium text-gray-900">
                Trip Value ($) *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  id="tripValue"
                  type="text"
                  value={tripValue}
                  onChange={(e) => {
                    const formatted = formatCurrency(e.target.value);
                    setTripValue(formatted);
                    if (errors.tripValue) {
                      setErrors(prev => ({ ...prev, tripValue: undefined }));
                    }
                  }}
                  disabled={loading}
                  maxLength={12}
                  className={`pl-9 px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300 ${
                    errors.tripValue ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-600'
                  }`}
                  style={{ 
                    height: '36px',
                    fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px'
                  }}
                  placeholder="0.00"
                />
              </div>
              {errors.tripValue && (
                <p className="mt-1 text-sm text-red-600">{errors.tripValue}</p>
              )}
            </div>

            <div>
              <label htmlFor="commissionRate" className="block mb-1 text-sm font-medium text-gray-900">
                Commission Rate (%) *
              </label>
              <input
                id="commissionRate"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={commissionRate}
                onChange={(e) => {
                  setCommissionRate(e.target.value);
                  if (errors.commissionRate) {
                    setErrors(prev => ({ ...prev, commissionRate: undefined }));
                  }
                }}
                disabled={loading}
                className={`px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300 ${
                  errors.commissionRate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-600'
                }`}
                style={{ 
                  height: '36px',
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  fontSize: '14px'
                }}
                placeholder="10"
              />
              {errors.commissionRate && (
                <p className="mt-1 text-sm text-red-600">{errors.commissionRate}</p>
              )}
            </div>
          </div>

          {/* Calculated Commission Amount */}
          <div>
            <label htmlFor="commissionAmount" className="block mb-1 text-sm font-medium text-gray-900">
              Commission Amount (Auto-calculated)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
              <input
                id="commissionAmount"
                type="text"
                value={commissionAmount}
                onChange={(e) => {
                  const formatted = formatCurrency(e.target.value);
                  setCommissionAmount(formatted);
                }}
                disabled={loading}
                maxLength={12}
                className="px-3 py-2 pl-9 w-full font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300"
                style={{ 
                  height: '36px',
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  fontSize: '14px'
                }}
                placeholder="0.00"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Automatically calculated from trip value and commission rate
            </p>
          </div>

          {/* Status and Dates */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="status" className="block mb-1 text-sm font-medium text-gray-900">
                Status *
              </label>
              <div className="relative">
                <select
                  id="status"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    if (e.target.value !== 'paid') {
                      setPaymentDate('');
                    }
                  }}
                  disabled={loading}
                  className="px-3 py-2 pr-10 w-full font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
                  style={{ 
                    height: '36px',
                    fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px'
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <svg
                  className="absolute right-3 top-1/2 w-4 h-4 text-gray-300 -translate-y-1/2 pointer-events-none"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div>
              <label htmlFor="expectedDate" className="block mb-1 text-sm font-medium text-gray-900">
                Expected Payment Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  id="expectedDate"
                  type="date"
                  value={expectedDate}
                  onChange={(e) => {
                    setExpectedDate(e.target.value);
                    if (errors.expectedDate) {
                      setErrors(prev => ({ ...prev, expectedDate: undefined }));
                    }
                  }}
                  disabled={loading}
                  className={`pl-9 px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 ${
                    errors.expectedDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-600'
                  }`}
                  style={{ 
                    height: '36px',
                    fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
              {errors.expectedDate && (
                <p className="mt-1 text-sm text-red-600">{errors.expectedDate}</p>
              )}
            </div>
          </div>

          {/* Payment Date (only show when status is 'paid') */}
          {status === 'paid' && (
            <div>
              <label htmlFor="paymentDate" className="block mb-1 text-sm font-medium text-gray-900">
                Actual Payment Date *
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                <input
                  id="paymentDate"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => {
                    setPaymentDate(e.target.value);
                    if (errors.paymentDate) {
                      setErrors(prev => ({ ...prev, paymentDate: undefined }));
                    }
                  }}
                  disabled={loading}
                  className={`pl-9 px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 ${
                    errors.paymentDate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-600'
                  }`}
                  style={{ 
                    height: '36px',
                    fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                    fontSize: '14px'
                  }}
                />
              </div>
              {errors.paymentDate && (
                <p className="mt-1 text-sm text-red-600">{errors.paymentDate}</p>
              )}
            </div>
          )}

          {/* Booking Reference */}
          <div>
            <label htmlFor="bookingReference" className="block mb-1 text-sm font-medium text-gray-900">
              Booking Reference
            </label>
            <div className="flex gap-2">
              <input
                id="bookingReference"
                type="text"
                value={bookingReference}
                onChange={(e) => setBookingReference(sanitizeText(e.target.value.toUpperCase()))}
                disabled={loading}
                maxLength={20}
                className="flex-1 px-3 py-2 font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300"
                style={{ 
                  height: '36px',
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  fontSize: '14px'
                }}
                placeholder="DW-XXXXXXXX"
              />
              <button
                type="button"
                onClick={generateBookingReference}
                disabled={loading}
                className="px-3 py-2 text-sm font-medium text-gray-700 whitespace-nowrap bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 disabled:opacity-50"
                style={{ height: '36px' }}
              >
                Generate
              </button>
            </div>
          </div>

          {/* Supplier */}
          <div>
            <label htmlFor="supplier" className="block mb-1 text-sm font-medium text-gray-900">
              Supplier
            </label>
            <input
              id="supplier"
              type="text"
              value={supplier}
              onChange={(e) => setSupplier(sanitizeText(capitalizeName(e.target.value)))}
              disabled={loading}
              maxLength={100}
              className="px-3 py-2 w-full font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300"
              style={{ 
                height: '36px',
                fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                fontSize: '14px'
              }}
              placeholder="Theme Park Resort"
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block mb-1 text-sm font-medium text-gray-900">
              Notes (Optional)
            </label>
            <div className="relative">
              <FileText className="absolute top-2.5 left-3 w-4 h-4 text-gray-400" />
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(sanitizeText(e.target.value))}
                disabled={loading}
                rows={2}
                maxLength={500}
                className="px-3 py-2 pl-10 w-full font-medium text-white bg-gray-700 rounded-lg border border-gray-600 transition-all duration-200 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500 placeholder:text-gray-300"
                style={{ 
                  fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif',
                  fontSize: '14px'
                }}
                placeholder="Add any additional notes about this commission..."
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn btn-magic"
            >
              {loading ? (
                <div className="flex justify-center items-center">
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Creating...
                </div>
              ) : (
                "Create Commission"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCommissionModal;

